import {
  CardContent, Divider, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../../../../../components/CustomTextField';
import { uploadAcademicImage } from '../../../../../api/talentsApi';
import useDebounce from '../../../../../hooks/useDebounce';
import { useGetAcademicQuery } from '../../../../../hooks/useTalentAcademic';
import { Trash } from '../../../../../icons/trash';
import STRING_KEYS from '../../../../../language/keys';
import { EducationFallback } from '../../../../../components/common/avatars/EducationFallback';
import { InsightLabelItem } from '../../../../../components/common/InsightLabelItem';
import { ErrorMessageSnackbar } from '../../../../../components/ErrorMessageSnackbar';
import { DateItem } from '../../DateItem';
import { InstitutionRow } from '../../InstitutionRow';
import InstitutionAutocomplete from '../../InstitutionAutocomplete';
import CopyableLabel from '../../../../../components/CopyableLabel';
import { SNACK_BAR_CLOSE_REASON } from '../../utils/consts';
import { VIEW_MODE } from '../../../../../consts/enums';
import { style } from './style';

type TalentEducationItemProps = {
  degree: string,
  fieldOfStudy: string,
  institutionLogo: string,
  institutionName: string,
  id: string,
  badges: BadgeItem[],
  onDeleteEducationChapter?: (id: string) => void,
  onEducationSelectHandler: (select: UpdateValues<string | boolean | null | DateObject>
    , id:string) => void,
  institutionId: string,
  disabled?: boolean,
  startDate: DateObject | null,
  endDate: DateObject | null,
  viewMode: ViewMode,
  onOpenCreateNewEducationModal?: (id:string) => void,
}

const TalentEducationItem = ({
  degree,
  fieldOfStudy,
  institutionLogo,
  badges,
  institutionName,
  onDeleteEducationChapter,
  id,
  onEducationSelectHandler,
  institutionId,
  disabled = false,
  startDate = null,
  endDate = null,
  viewMode,
  onOpenCreateNewEducationModal,
}: TalentEducationItemProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [academicSearch, setAcademicSearch] = useState<string>('');
  const [academicInstitutGetFrom, setAcademicInstituteGetFrom] = useState<string>('');
  const { debouncedValue: academicDebounce, isDebouncing } = useDebounce(academicSearch);
  const { academics, isLoading }  = useGetAcademicQuery(academicDebounce, (type) => {
    setAcademicInstituteGetFrom(type);
  });

  const [isUploading, setIsUploadLoading] = useState<boolean>(false);
  const canUploadImage = !institutionId || institutionId.length === 0;
  const [errorUploadImage, setErrorUploadImage] = useState<
  {error: boolean, errorMessage: string, }>({
    error: false,
    errorMessage: '',
  });
  const handleAcademicChange = (event:React.SyntheticEvent, newInputValue:string) => {
    setAcademicSearch(newInputValue);
  };

  const handleSelectEducationInsitut = (e: React.SyntheticEvent, newVal: Academic) => {
    if (!newVal) return;
    onEducationSelectHandler(
      {
        institutionName: newVal.name,
        institutionLogo: newVal.logo,
        institutionId: newVal.id,
      },
      id,
    );
  };

  const handleInputUploadImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files ? e.target.files[0] : '';
    const formData = new FormData();
    formData.append('file', myFile);
    setIsUploadLoading(true);
    uploadAcademicImage(formData)
      .then((data: { fileUrl: string }) => {
        onEducationSelectHandler({ institutionLogo: data.fileUrl }, id);
      })
      .catch((error) => {
        setErrorUploadImage({
          error: true,
          errorMessage: error.message,
        });
      })
      .finally(() => {
        setIsUploadLoading(false);
      });
  }, [setIsUploadLoading, setErrorUploadImage, id, onEducationSelectHandler]);

  const onChangeValues = (event:React.SyntheticEvent, newVal:string) => {
    onEducationSelectHandler(
      {
        institutionName: newVal,
        institutionLogo: '',
        institutionId: '',
      },
      id,
    );
  };

  const handleSnackbarClose = (e: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === SNACK_BAR_CLOSE_REASON.CLICK_AWAY) {
      return;
    }
    setErrorUploadImage({ error: false, errorMessage: '' });
  };

  const canDelete = typeof onDeleteEducationChapter === 'function' &&
  viewMode === VIEW_MODE.EDITABLE;
  const fieldModeStyle =
    viewMode === VIEW_MODE.READ_ONLY ? {} :  style.card.CardContent.dateBlock.boxInner;

  // todo: if cannot upload image & there is no image = show default image
  // todo: also in experience

  return (
    <>
      {
        errorUploadImage.error && (
          <ErrorMessageSnackbar
            showError={  errorUploadImage.error }
            errorMessage={ errorUploadImage.errorMessage }
            onClose={ handleSnackbarClose }
          />
        )
      }
      <CardContent sx={ style.card.CardContent.cardContentContainer.sx }>
        <Box sx={ style.card.CardContent.fullWidthField }>
          <CustomTextField
            viewMode={ viewMode }
            value={ degree }
            name="degree"
            id="education-degree"
            disabled={ disabled }
            onChange={ (e:React.ChangeEvent<HTMLInputElement>) => {
              onEducationSelectHandler(
                { [e.target.name]: e.target.value },
                id,
              );
            } }
            label={ translate(STRING_KEYS.DEGREE) }
            fullWidth
          />
        </Box>
        <Box sx={ style.card.CardContent.fullWidthField }>
          <CustomTextField
            viewMode={ viewMode }
            value={ fieldOfStudy }
            name="fieldOfStudy"
            id="education-field-of-study"
            disabled={ disabled }
            onChange={ (e:React.ChangeEvent<HTMLInputElement>) => {
              onEducationSelectHandler(
                { [e.target.name]: e.target.value },
                id,
              );
            } }
            label={ translate(STRING_KEYS.FIELD_OF_STUDY) }
            fullWidth
          />
        </Box>
        <InstitutionRow
          logo={ institutionLogo }
          isloading={ isUploading }
          onChangeImage={ handleInputUploadImage }
          canUploadImage={ !disabled && canUploadImage }
          disabled
          FallBack={ EducationFallback }
          viewMode={ viewMode }
        >
          <Box sx={ style.autoCompleteBox.sx } style={ style.autoCompleteBox }>
            <InstitutionAutocomplete
              id={ id }
              viewMode={ viewMode }
              listAutocomplete={ academics }
              onInputValueChange={ handleAcademicChange }
              onOptionSelected={ handleSelectEducationInsitut }
              onChangeValues={ onChangeValues }
              valueName={ institutionName }
              label={ translate(STRING_KEYS.EDUCATION_INSTITUTE) }
              disabled={ disabled }
              isLoading={ isLoading || isDebouncing }
              academicInstitutGetFrom={ academicInstitutGetFrom }
              onOpenCreateNewEducationModal={ onOpenCreateNewEducationModal }
            />
            {institutionId && (
              <CopyableLabel labelText={ translate(STRING_KEYS.ID, { id: institutionId }) }
                dataToCopy={ institutionId }
                variant="caption"
                maxWidth="12rem"
                textColor="text.secondary"
              />
            )}
          </Box>
        </InstitutionRow>
        {/* box start */}
        <Box sx={ style.card.CardContent.dateBlock.boxMain }>
          <Box sx={ fieldModeStyle }>
            <Box>
              <Typography
                width={ style.card.CardContent.dateBlock.typography.width }
                variant={ style.card.CardContent.dateBlock.typography.variant }
                sx={ style.card.CardContent.dateBlock.typography.sx }
              >
                { translate(STRING_KEYS.START_DATE_TITLE) }
              </Typography>
              <DateItem
                companyId={ id }
                onDateValueChanged={ onEducationSelectHandler }
                name="startDate"
                disabled={ false }
                dateObj={ startDate }
                viewMode={ viewMode }
              />
            </Box>
          </Box>
          {/* box end */}
          <Box sx={ fieldModeStyle }>
            <Box>
              <Typography
                width={ style.card.CardContent.dateBlock.typography.width }
                variant={ style.card.CardContent.dateBlock.typography.variant }
                sx={ style.card.CardContent.dateBlock.typography.dateBLock }
              >
                { translate(STRING_KEYS.END_DATE_TITLE) }
              </Typography>
              <Box sx={ style.card.CardContent.dateBlock.boxMain }>
                <Box sx={ style.card.CardContent.textField.field }>
                  <DateItem
                    companyId={ id }
                    onDateValueChanged={ onEducationSelectHandler }
                    name="endDate"
                    disabled={ false }
                    dateObj={ endDate }
                    viewMode={ viewMode }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* label block */}
        <Typography
          style={ style.card.CardContent.label.style }
          sx={ style.card.CardContent.label.sx  }
        >{translate(STRING_KEYS.INSIGHT_LABEL)}
        </Typography>
        <InsightLabelItem
          badges={  badges }
          onDateValueChanged={ onEducationSelectHandler }
          companyId={ id }
          disabled={ disabled }
          showClearButton={ !disabled }
          viewMode={ viewMode }
        />
        <Divider />

        {/* trash block */}
        {canDelete && (
          <Box onClick={ () => onDeleteEducationChapter(id) }
            sx={ style.card.CardContent.boxTrash.sx }
          >
            <Trash color={ style.card.CardContent.boxTrash.color } />
          </Box>
        )}
      </CardContent>
      <Divider />
    </>
  );
};

TalentEducationItem.defaultProps = {
  onDeleteEducationChapter: undefined,
  onOpenCreateNewEducationModal: undefined,
  disabled: false,
};

export default React.memo(TalentEducationItem);
